import { useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Text } from '@antimatterhq/ui/components/typography';
import { DatasourceUpgradeTableDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { cn } from '@/lib/utils';
import { getFieldTableRows, mergeFieldSpecs } from '@/lib/datasource';
import { filterNulls } from '@/utils/filterNulls';
import { FieldSpecReview } from '@/components/dataSources/review';

export const DatasourceUpgradeDiffTable = ({
  diff,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  diff: DatasourceUpgradeTableDiff;
  onAccept: () => void;
  onReject: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}) => {
  const action = useMemo(() => {
    if (!diff.upgradePresetValue && diff.currentPresetValue) {
      return 'delete';
    }
    return 'add';
  }, [diff.currentPresetValue, diff.upgradePresetValue]);

  const accepted = useMemo(() => {
    if (diff.status !== 'confirmed') {
      return false;
    }
    // if added then accepted means that there shouldn't be an omitted field in the upgrade
    if (action === 'add') {
      return !diff.upgradeOmitted;
    }

    // if accepted a delete then there should not be an add table
    return !diff.upgradedAddTable;
  }, [action, diff.status, diff.upgradeOmitted, diff.upgradedAddTable]);

  const rejected = useMemo(() => {
    if (diff.status !== 'confirmed') {
      return false;
    }
    // if add is rejected then there should be an omitted field in the upgrade
    if (action === 'add') {
      return diff.upgradeOmitted;
    }

    // if accepted a delete then there should be an add table
    return !!diff.upgradedAddTable;
  }, [action, diff.status, diff.upgradeOmitted, diff.upgradedAddTable]);

  const currentFields = useMemo(() => {
    if (!diff.currentValue || !diff.currentPresetValue) {
      return undefined;
    }

    return getFieldTableRows(
      mergeFieldSpecs(
        filterNulls(diff.currentPresetValue.fields),
        filterNulls(diff.currentValue.addFields),
        diff.currentValue.omitFields || undefined,
      ),
    );
  }, [diff.currentValue, diff.currentPresetValue]);

  const upgradeFields = useMemo(() => {
    if (action === 'delete') {
      return currentFields?.map((field) => ({ ...field, strikethrough: true }));
    }

    if (!diff.upgradeValue || !diff.upgradePresetValue) {
      return undefined;
    }

    return getFieldTableRows(
      mergeFieldSpecs(
        filterNulls(diff.upgradePresetValue.fields),
        filterNulls(diff.upgradeValue.addFields),
        diff.upgradeValue.omitFields || undefined,
      ),
    );
  }, [currentFields, action, diff.upgradeValue, diff.upgradePresetValue]);

  const tableName = useMemo(() => {
    return diff.currentPresetValue?.name || diff.upgradePresetValue?.name;
  }, [diff.currentPresetValue, diff.upgradePresetValue]);

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`table-diff-root-${diff.tableName}`}
    >
      <div className="flex w-full items-center gap-6">
        <div
          className="flex-1 text-center"
          data-testid="table-diff-current-container"
        >
          <div
            className={cn(
              'flex items-center',
              !diff.currentPresetValue && 'justify-center',
            )}
          >
            {currentFields && (
              <div>
                <div className="text-left">
                  <Text bold>Table:</Text> <Text>{tableName}</Text>
                </div>
                <div className="h-[200px] flex-1 overflow-y-auto text-left">
                  <FieldSpecReview fields={currentFields} />
                </div>
              </div>
            )}

            {!diff.currentPresetValue && (
              <>
                <div className="w-6"></div>
                <Text className="italic" color="secondary">
                  (no table)
                </Text>
              </>
            )}

            <div className="w-6">
              {rejected && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="table-diff-rejected-icon"
                />
              )}
            </div>
          </div>
        </div>
        <div
          className="flex-1 text-center"
          data-testid="table-diff-upgrade-container"
        >
          <div className="flex items-center">
            <div className="flex-1">
              <div>
                <Text
                  className={cn(
                    'block text-left',
                    action === 'delete' && 'line-through',
                  )}
                >
                  <span>Table:</span> <span>{tableName}</span>
                </Text>
                <div className="h-[200px] flex-1 overflow-y-auto text-left">
                  <FieldSpecReview fields={upgradeFields} />
                </div>
              </div>
            </div>
            <div className="w-6">
              {accepted && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="table-diff-accepted-icon"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          <Button
            variant="default"
            onClick={onReject}
            disabled={rejected || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-table-reject-btn"
          >
            {action === 'delete' && 'Keep existing table'}
            {action === 'add' && 'Ignore new table'}
          </Button>
        </div>
        <div className="flex-1 text-center">
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={accepted || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-table-accept-btn"
          >
            {action === 'delete' && 'Remove table'}
            {action === 'add' && 'Add table'}
          </Button>
        </div>
      </div>
    </div>
  );
};
